import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogActions,
  TextField,
  Button,
  Alert,
  Typography,
  Box,
  Stack,
} from '@mui/material';
import { CheckCircle, Warning } from '@mui/icons-material';
import { supabase } from '@/integrations/supabase/client';
import { OWNERSHIP_CONTACT_EMAIL } from '@/utils/constants';

interface ClaimModalProps {
  isOpen: boolean;
  onClose: () => void;
  sequenceId: string;
  onSuccess: () => void;
}

interface FormData {
  name: string;
  email: string;
  verificationDetails: string;
}

const ClaimModal: React.FC<ClaimModalProps> = ({
  isOpen,
  onClose,
  sequenceId,
  onSuccess
}) => {
  const [formData, setFormData] = useState<FormData>({
    name: '',
    email: '',
    verificationDetails: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      // Submit claim via API endpoint
      const response = await fetch('/api/sequences/claim', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          sequenceId,
          name: formData.name,
          email: formData.email,
          verificationDetails: formData.verificationDetails,
          userId: null // For now, allow anonymous claims
        }),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Failed to submit claim');
      }

      setSuccess(true);
      
      // Auto-close after success message is shown
      setTimeout(() => {
        onSuccess();
      }, 3000);

    } catch (err: any) {
      console.error('Error submitting claim:', err);
      setError(err.message || 'Failed to submit claim. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({ name: '', email: '', verificationDetails: '' });
    setError('');
    setSuccess(false);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  if (success) {
    return (
      <Dialog open={isOpen} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CheckCircle color="success" />
          Claim Submitted Successfully!
        </DialogTitle>
        
        <DialogContent>
          <Stack spacing={3}>
            <Alert severity="success">
              We've received your ownership claim for this sequence.
            </Alert>
            
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                What happens next:
              </Typography>
              <Stack component="ul" spacing={1} sx={{ pl: 2, listStyleType: 'disc' }}>
                <Typography component="li" variant="body2" color="text.secondary">
                  Our team will review your claim and verification details
                </Typography>
                <Typography component="li" variant="body2" color="text.secondary">
                  We'll contact you at <strong>{formData.email}</strong> with updates
                </Typography>
                <Typography component="li" variant="body2" color="text.secondary">
                  The review process typically takes 2-3 business days
                </Typography>
              </Stack>
              
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                For any questions, please contact{' '}
                <Typography 
                  component="a" 
                  href={`mailto:${OWNERSHIP_CONTACT_EMAIL}`}
                  color="primary"
                  sx={{ textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
                >
                  {OWNERSHIP_CONTACT_EMAIL}
                </Typography>
              </Typography>
            </Box>
          </Stack>
        </DialogContent>
        
        <DialogActions>
          <Button onClick={handleClose} variant="contained" fullWidth>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  return (
    <Dialog open={isOpen} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        Claim Sequence Ownership
      </DialogTitle>
      
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          If you are the original creator of this sequence, please provide your 
          contact information and verification details below.
        </Typography>

        <Stack component="form" onSubmit={handleSubmit} spacing={3}>
          <TextField
            name="name"
            label="Your Name"
            type="text"
            value={formData.name}
            onChange={handleChange}
            placeholder="Enter your full name"
            required
            fullWidth
            variant="outlined"
          />

          <TextField
            name="email"
            label="Contact Email"
            type="email"
            value={formData.email}
            onChange={handleChange}
            placeholder="your@email.com"
            required
            fullWidth
            variant="outlined"
          />

          <Box>
            <TextField
              name="verificationDetails"
              label="Verification Details"
              value={formData.verificationDetails}
              onChange={handleChange}
              placeholder="Please provide details to verify your ownership (e.g., when you created this sequence, details about your display setup, original file information, etc.)"
              required
              fullWidth
              multiline
              rows={4}
              variant="outlined"
            />
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              The more specific details you can provide, the faster we can verify your claim.
            </Typography>
          </Box>

          {error && (
            <Alert severity="error" icon={<Warning />}>
              {error}
            </Alert>
          )}
        </Stack>
      </DialogContent>
      
      <DialogActions sx={{ p: 3, gap: 1 }}>
        <Button
          variant="outlined"
          onClick={handleClose}
          disabled={loading}
          sx={{ flex: 1 }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={loading}
          sx={{ flex: 1 }}
        >
          {loading ? 'Submitting...' : 'Submit Claim'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ClaimModal; 